import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, Plus, Download, FileText, Calendar, Clock, 
  MapPin, User, Phone, Building2, Stethoscope, Pen, Trash2, Eye
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const IncidentReportsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [reports, setReports] = useState([]);
  const [children, setChildren] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterClassroom, setFilterClassroom] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  
  const [formData, setFormData] = useState({
    child_id: '',
    child_name: '',
    child_birthdate: '',
    child_home_phone: '',
    parent1: { name: '', home_phone: '', work_phone: '' },
    parent2: { name: '', home_phone: '', work_phone: '' },
    parent_notification: '',
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    incident_am_pm: 'AM',
    location_type: 'Indoors',
    location_other: '',
    adult_witnesses: '',
    incident_description: '',
    equipment_involved: '',
    medical_awareness: '',
    hospital_name: '',
    physician_name: '',
    hospital_address: '',
    medical_evaluation_description: '',
    medical_treatment_provided: false,
    medical_treatment_description: '',
    classroom: '',
    completed_by_name: '',
    completed_by_title: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, childrenRes, classroomsRes] = await Promise.all([
        axios.get(`${API}/incident-reports`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/classrooms`, getAuthHeaders())
      ]);
      setReports(reportsRes.data);
      setChildren(childrenRes.data);
      setClassrooms(classroomsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChildSelect = (childId) => {
    const child = children.find(c => c.id === childId);
    if (child) {
      setFormData({
        ...formData,
        child_id: childId,
        child_name: `${child.first_name} ${child.last_name}`,
        child_birthdate: child.date_of_birth || child.dob || '',
        classroom: child.classroom || '',
        parent1: {
          name: child.parent1_name || '',
          home_phone: child.parent1_phone || '',
          work_phone: child.parent1_work_phone || ''
        },
        parent2: {
          name: child.parent2_name || '',
          home_phone: child.parent2_phone || '',
          work_phone: child.parent2_work_phone || ''
        }
      });
    }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    
    if (!formData.child_id || !formData.incident_description) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API}/incident-reports`, formData, getAuthHeaders());
      toast.success('Incident report created');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create report');
    }
  };

  const handleSignReport = async (reportId) => {
    const name = prompt('Enter your name and title to sign:');
    if (!name) return;
    
    try {
      await axios.post(`${API}/incident-reports/${reportId}/sign?signature_name=${encodeURIComponent(name)}`, {}, getAuthHeaders());
      toast.success('Report signed');
      fetchData();
    } catch (error) {
      toast.error('Failed to sign report');
    }
  };

  const handleDownloadPDF = async (reportId) => {
    try {
      const response = await axios.get(`${API}/incident-reports/${reportId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_report_${reportId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await axios.delete(`${API}/incident-reports/${reportId}`, getAuthHeaders());
      toast.success('Report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const resetForm = () => {
    setFormData({
      child_id: '',
      child_name: '',
      child_birthdate: '',
      child_home_phone: '',
      parent1: { name: '', home_phone: '', work_phone: '' },
      parent2: { name: '', home_phone: '', work_phone: '' },
      parent_notification: '',
      incident_date: new Date().toISOString().split('T')[0],
      incident_time: '',
      incident_am_pm: 'AM',
      location_type: 'Indoors',
      location_other: '',
      adult_witnesses: '',
      incident_description: '',
      equipment_involved: '',
      medical_awareness: '',
      hospital_name: '',
      physician_name: '',
      hospital_address: '',
      medical_evaluation_description: '',
      medical_treatment_provided: false,
      medical_treatment_description: '',
      classroom: '',
      completed_by_name: '',
      completed_by_title: ''
    });
  };

  const filteredReports = reports.filter(r => {
    if (filterClassroom !== 'all' && r.classroom !== filterClassroom) return false;
    if (filterDate && r.incident_date !== filterDate) return false;
    return true;
  });

  const isStaff = user?.role === 'admin' || user?.role === 'provider';

  return (
    <DashboardLayout title="Incident Reports">
      <div className="space-y-6" data-testid="incident-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-muted-foreground">
              DCF-F-CFS0055 compliant incident reporting for regulated child care
            </p>
          </div>
          {isStaff && (
            <Button onClick={() => setDialogOpen(true)} data-testid="new-incident-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Incident Report
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Filter by Classroom</Label>
                <Select value={filterClassroom} onValueChange={setFilterClassroom}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classrooms</SelectItem>
                    {classrooms.map(c => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[180px]">
                <Label className="text-xs text-muted-foreground">Filter by Date</Label>
                <Input 
                  type="date" 
                  value={filterDate} 
                  onChange={e => setFilterDate(e.target.value)}
                />
              </div>
              {(filterClassroom !== 'all' || filterDate) && (
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={() => { setFilterClassroom('all'); setFilterDate(''); }}>
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        <div className="grid gap-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center">Loading...</CardContent></Card>
          ) : filteredReports.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No incident reports found</p>
              </CardContent>
            </Card>
          ) : (
            filteredReports.map(report => (
              <Card key={report.id} className="hover:shadow-md transition-shadow" data-testid={`incident-${report.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{report.child_name}</h3>
                          <Badge variant={report.status === 'signed' ? 'default' : 'secondary'}>
                            {report.status === 'signed' ? 'Signed' : 'Draft'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {report.incident_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {report.incident_time} {report.incident_am_pm}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {report.location_type}
                          </span>
                          {report.classroom && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {report.classroom}
                            </span>
                          )}
                        </div>
                        <p className="text-sm mt-2 line-clamp-2">{report.incident_description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedReport(report); setViewDialogOpen(true); }}
                        data-testid={`view-incident-${report.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDownloadPDF(report.id)}
                        data-testid={`download-incident-${report.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {isStaff && report.status !== 'signed' && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleSignReport(report.id)}
                          data-testid={`sign-incident-${report.id}`}
                        >
                          <Pen className="w-4 h-4" />
                        </Button>
                      )}
                      {user?.role === 'admin' && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteReport(report.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Incident Report Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                New Incident Report
              </DialogTitle>
              <DialogDescription>
                DCF-F-CFS0055 - Complete all sections as required
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateReport}>
              <ScrollArea className="h-[60vh] pr-4">
                <Tabs defaultValue="child" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 h-auto gap-1 mb-4">
                    <TabsTrigger value="child">Child Info</TabsTrigger>
                    <TabsTrigger value="parent">Parent Info</TabsTrigger>
                    <TabsTrigger value="incident">Incident</TabsTrigger>
                    <TabsTrigger value="medical">Medical</TabsTrigger>
                    <TabsTrigger value="signature">Signature</TabsTrigger>
                  </TabsList>

                  {/* Child Information Tab */}
                  <TabsContent value="child" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Select Child *</Label>
                        <Select value={formData.child_id} onValueChange={handleChildSelect}>
                          <SelectTrigger data-testid="child-select">
                            <SelectValue placeholder="Select a child" />
                          </SelectTrigger>
                          <SelectContent>
                            {children.map(child => (
                              <SelectItem key={child.id} value={child.id}>
                                {child.first_name} {child.last_name} - {child.classroom || 'No classroom'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Child Name</Label>
                        <Input value={formData.child_name} disabled className="bg-muted" />
                      </div>
                      <div>
                        <Label>Birthdate</Label>
                        <Input value={formData.child_birthdate} disabled className="bg-muted" />
                      </div>
                      <div>
                        <Label>Classroom</Label>
                        <Input value={formData.classroom} disabled className="bg-muted" />
                      </div>
                      <div>
                        <Label>Home Telephone</Label>
                        <Input 
                          value={formData.child_home_phone} 
                          onChange={e => setFormData({...formData, child_home_phone: e.target.value})}
                          placeholder="(xxx) xxx-xxxx"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Parent Information Tab */}
                  <TabsContent value="parent" className="space-y-4">
                    <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                      <h4 className="font-medium">Parent/Guardian 1</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input 
                            value={formData.parent1.name}
                            onChange={e => setFormData({...formData, parent1: {...formData.parent1, name: e.target.value}})}
                          />
                        </div>
                        <div>
                          <Label>Home Phone</Label>
                          <Input 
                            value={formData.parent1.home_phone}
                            onChange={e => setFormData({...formData, parent1: {...formData.parent1, home_phone: e.target.value}})}
                          />
                        </div>
                        <div>
                          <Label>Work Phone</Label>
                          <Input 
                            value={formData.parent1.work_phone}
                            onChange={e => setFormData({...formData, parent1: {...formData.parent1, work_phone: e.target.value}})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                      <h4 className="font-medium">Parent/Guardian 2</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input 
                            value={formData.parent2.name}
                            onChange={e => setFormData({...formData, parent2: {...formData.parent2, name: e.target.value}})}
                          />
                        </div>
                        <div>
                          <Label>Home Phone</Label>
                          <Input 
                            value={formData.parent2.home_phone}
                            onChange={e => setFormData({...formData, parent2: {...formData.parent2, home_phone: e.target.value}})}
                          />
                        </div>
                        <div>
                          <Label>Work Phone</Label>
                          <Input 
                            value={formData.parent2.work_phone}
                            onChange={e => setFormData({...formData, parent2: {...formData.parent2, work_phone: e.target.value}})}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label>How were parent(s)/guardian(s) notified? (Date, time, description)</Label>
                      <Textarea 
                        value={formData.parent_notification}
                        onChange={e => setFormData({...formData, parent_notification: e.target.value})}
                        placeholder="e.g., Called mother at 2:30 PM on 01/21/2026..."
                        rows={3}
                      />
                    </div>
                  </TabsContent>

                  {/* Incident Information Tab */}
                  <TabsContent value="incident" className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Date *</Label>
                        <Input 
                          type="date"
                          value={formData.incident_date}
                          onChange={e => setFormData({...formData, incident_date: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Time *</Label>
                        <Input 
                          type="time"
                          value={formData.incident_time}
                          onChange={e => setFormData({...formData, incident_time: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>AM/PM</Label>
                        <Select value={formData.incident_am_pm} onValueChange={v => setFormData({...formData, incident_am_pm: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Location *</Label>
                        <Select value={formData.location_type} onValueChange={v => setFormData({...formData, location_type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Indoors">Indoors</SelectItem>
                            <SelectItem value="Outdoors">Outdoors</SelectItem>
                            <SelectItem value="Vehicle">Vehicle</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.location_type === 'Other' && (
                        <div>
                          <Label>Specify Location</Label>
                          <Input 
                            value={formData.location_other}
                            onChange={e => setFormData({...formData, location_other: e.target.value})}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Names of Adult Witnesses</Label>
                      <Input 
                        value={formData.adult_witnesses}
                        onChange={e => setFormData({...formData, adult_witnesses: e.target.value})}
                        placeholder="e.g., Jane Smith (Teacher), John Doe (Assistant)"
                      />
                    </div>
                    <div>
                      <Label>Description of Incident * (Nature, extent of injury, activity, action taken)</Label>
                      <Textarea 
                        value={formData.incident_description}
                        onChange={e => setFormData({...formData, incident_description: e.target.value})}
                        placeholder="Describe what happened, the injury, what the child was doing, and what actions were taken..."
                        rows={5}
                        required
                      />
                    </div>
                    <div>
                      <Label>Equipment Involved (Brand name, type, age rating)</Label>
                      <Input 
                        value={formData.equipment_involved}
                        onChange={e => setFormData({...formData, equipment_involved: e.target.value})}
                        placeholder="e.g., Little Tikes Climber, ages 2-5"
                      />
                    </div>
                  </TabsContent>

                  {/* Medical Information Tab */}
                  <TabsContent value="medical" className="space-y-4">
                    <div>
                      <Label>How was the center made aware of medical evaluation? (Date, time, description)</Label>
                      <Textarea 
                        value={formData.medical_awareness}
                        onChange={e => setFormData({...formData, medical_awareness: e.target.value})}
                        placeholder="e.g., Parent called at 4:00 PM to inform us they took child to urgent care..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Hospital/Clinic Name</Label>
                        <Input 
                          value={formData.hospital_name}
                          onChange={e => setFormData({...formData, hospital_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Physician Name</Label>
                        <Input 
                          value={formData.physician_name}
                          onChange={e => setFormData({...formData, physician_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Hospital/Clinic Address</Label>
                      <Input 
                        value={formData.hospital_address}
                        onChange={e => setFormData({...formData, hospital_address: e.target.value})}
                        placeholder="Street, City, State, Zip"
                      />
                    </div>
                    <div>
                      <Label>Description of Medical Evaluation</Label>
                      <Textarea 
                        value={formData.medical_evaluation_description}
                        onChange={e => setFormData({...formData, medical_evaluation_description: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="medical_treatment"
                        checked={formData.medical_treatment_provided}
                        onCheckedChange={checked => setFormData({...formData, medical_treatment_provided: checked})}
                      />
                      <Label htmlFor="medical_treatment">Was medical treatment provided by a medical professional?</Label>
                    </div>
                    {formData.medical_treatment_provided && (
                      <div>
                        <Label>Describe the treatment provided</Label>
                        <Textarea 
                          value={formData.medical_treatment_description}
                          onChange={e => setFormData({...formData, medical_treatment_description: e.target.value})}
                          rows={3}
                        />
                      </div>
                    )}
                  </TabsContent>

                  {/* Signature / Center Representative Tab */}
                  <TabsContent value="signature" className="space-y-4">
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                      <p className="text-sm text-amber-800">
                        <strong>Center Representative / Certified Provider</strong><br/>
                        Enter your name and title as the employee completing this incident report.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Your Name (Typed) *</Label>
                        <Input 
                          value={formData.completed_by_name}
                          onChange={e => setFormData({...formData, completed_by_name: e.target.value})}
                          placeholder="e.g., Jane Smith"
                          required
                        />
                      </div>
                      <div>
                        <Label>Your Title *</Label>
                        <Input 
                          value={formData.completed_by_title}
                          onChange={e => setFormData({...formData, completed_by_title: e.target.value})}
                          placeholder="e.g., Lead Teacher, Director, Assistant"
                          required
                        />
                      </div>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg mt-4">
                      <p className="text-xs text-muted-foreground">
                        By submitting this form, you certify that the information provided is accurate and complete to the best of your knowledge.
                        A physical signature may be required on the printed form.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </ScrollArea>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-incident-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Create Report
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Incident Report Details</DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <ScrollArea className="h-[60vh] pr-4">
                <div className="space-y-6">
                  {/* Child Info */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" /> Child Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> {selectedReport.child_name}</div>
                      <div><span className="text-muted-foreground">Birthdate:</span> {selectedReport.child_birthdate}</div>
                      <div><span className="text-muted-foreground">Classroom:</span> {selectedReport.classroom}</div>
                    </div>
                  </div>

                  {/* Incident Info */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-4 h-4" /> Incident Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                      <div><span className="text-muted-foreground">Date:</span> {selectedReport.incident_date}</div>
                      <div><span className="text-muted-foreground">Time:</span> {selectedReport.incident_time} {selectedReport.incident_am_pm}</div>
                      <div><span className="text-muted-foreground">Location:</span> {selectedReport.location_type}</div>
                    </div>
                    <div className="text-sm mb-2">
                      <span className="text-muted-foreground">Witnesses:</span> {selectedReport.adult_witnesses || 'None listed'}
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Description:</span>
                      <p className="mt-1 whitespace-pre-wrap">{selectedReport.incident_description}</p>
                    </div>
                  </div>

                  {/* Medical Info */}
                  {(selectedReport.hospital_name || selectedReport.medical_evaluation_description) && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700">
                        <Stethoscope className="w-4 h-4" /> Medical Information
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div><span className="text-muted-foreground">Hospital:</span> {selectedReport.hospital_name || 'N/A'}</div>
                        <div><span className="text-muted-foreground">Physician:</span> {selectedReport.physician_name || 'N/A'}</div>
                      </div>
                      {selectedReport.medical_evaluation_description && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Evaluation:</span>
                          <p className="mt-1">{selectedReport.medical_evaluation_description}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Signature */}
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold mb-3">Signature</h4>
                    {selectedReport.status === 'signed' ? (
                      <div className="text-sm">
                        <div><span className="text-muted-foreground">Signed by:</span> {selectedReport.signature_name}</div>
                        <div><span className="text-muted-foreground">Date:</span> {selectedReport.signed_at?.split('T')[0]}</div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not yet signed</p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            )}

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              {selectedReport && (
                <Button onClick={() => handleDownloadPDF(selectedReport.id)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default IncidentReportsPage;
